import Konva from 'konva';
import React, { useRef, useCallback } from 'react';
import { useMapCanvasStage, useMapCanvasStagePosition } from '../contexts/MapCanvasContext';

export interface UseMapViewHandlersOptions {
  stageRef: React.MutableRefObject<Konva.Stage | null>;
}

export interface UseMapViewHandlersReturn {
  onWheel: (event: Konva.KonvaEventObject<WheelEvent>) => void;
  onDragMove: (event: Konva.KonvaEventObject<DragEvent>) => void;
  onMouseDown: (event: Konva.KonvaPointerEvent) => void;
  onMouseMove: (event: Konva.KonvaPointerEvent) => void;
  onDragEnd: (event: Konva.KonvaEventObject<DragEvent>) => void;
  onTouchMove: (event: Konva.KonvaEventObject<TouchEvent>) => void;
  onTouchEnd: (event: Konva.KonvaEventObject<TouchEvent>) => void;
  cursor: string;
}

export const useMapViewHandlers = (
  options: UseMapViewHandlersOptions
): UseMapViewHandlersReturn => {
  const { stageRef } = options;
  const { stagePosition } = useMapCanvasStagePosition();
  const {
    updateStagePosition,
    minScale: MIN_SCALE,
    maxScale: MAX_SCALE
  } = useMapCanvasStage();

  const cursor = 'move';

  // Refs for tracking pinch-to-zoom state
  const lastTouchCenterRef = useRef<{ x: number; y: number } | null>(null);
  const lastTouchDistRef = useRef<number>(0);
  const pinchZoomingRef = useRef<boolean>(false);

  // Refs for throttling trackpad wheel events
  const lastWheelTimeRef = useRef<number>(0);
  const wheelThrottleMs = 50; // Process trackpad events at most every 50ms

  const getDistance = (
    p1: { x: number; y: number },
    p2: { x: number; y: number }
  ) => {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  };

  const getCenter = (
    p1: { x: number; y: number },
    p2: { x: number; y: number }
  ) => ({
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2
  });

  /** 🖱 Wheel Zoom (Desktop) */
  const handleWheel = useCallback(
    (event: Konva.KonvaEventObject<WheelEvent>) => {
      if (pinchZoomingRef.current) return; // Prevent conflict

      event.evt.preventDefault();
      const stage = stageRef.current;
      if (!stage) return;

      const evt = event.evt;
      const isPixelMode = evt.deltaMode === 0;

      // Throttle trackpad/pixel-mode events to prevent speed-dependent zoom
      // Mouse wheel (LINE mode) is not throttled since it fires discrete events
      if (isPixelMode) {
        const now = performance.now();
        if (now - lastWheelTimeRef.current < wheelThrottleMs) {
          return; // Skip this event, too soon after last one
        }
        lastWheelTimeRef.current = now;
      }

      const oldScale = stage.scaleX();
      const pointer = stage.getPointerPosition();
      if (!pointer) return;

      const mousePointTo = {
        x: (pointer.x - stage.x()) / oldScale,
        y: (pointer.y - stage.y()) / oldScale
      };

      // Normalize wheel delta to handle both mouse wheel and trackpad
      // Mouse wheel: deltaMode=1 (LINE) - discrete notched scrolling
      // Trackpad/Pixel: deltaMode=0 (PIXEL) - continuous smooth scrolling (throttled above)
      let zoomFactor: number;

      if (!isPixelMode) {
        // DOM_DELTA_LINE (mouse wheel with notches) - use standard zoom
        zoomFactor = evt.deltaY < 0 ? 1.1 : 1 / 1.1;
      } else {
        // DOM_DELTA_PIXEL (trackpad or high-precision mouse wheel)
        // Fixed zoom factor per throttled event
        zoomFactor = evt.deltaY < 0 ? 1.08 : 1 / 1.08;
      }

      const newScale = oldScale * zoomFactor;
      const boundedScale = Math.min(Math.max(newScale, MIN_SCALE), MAX_SCALE);

      if (boundedScale !== oldScale) {
        const newPos = {
          x: pointer.x - mousePointTo.x * boundedScale,
          y: pointer.y - mousePointTo.y * boundedScale,
          scale: boundedScale
        };

        stage.position({ x: newPos.x, y: newPos.y });
        stage.scale({ x: boundedScale, y: boundedScale });
        updateStagePosition(newPos);
      }
    },
    [stagePosition]
  );

  /** 📱 Pinch-to-Zoom (Mobile) */
  const handleTouchMove = (event: Konva.KonvaEventObject<TouchEvent>) => {
    const stage = stageRef.current;
    if (!stage) return;

    const touch1 = event.evt.touches[0];
    const touch2 = event.evt.touches[1];

    // Handle pinch-to-zoom
    if (touch1 && touch2) {
      event.evt.preventDefault();
      pinchZoomingRef.current = true;
      stage.draggable(false); // Disable dragging during zoom

      const p1 = { x: touch1.clientX, y: touch1.clientY };
      const p2 = { x: touch2.clientX, y: touch2.clientY };

      if (!lastTouchCenterRef.current) {
        lastTouchCenterRef.current = getCenter(p1, p2);
        return;
      }

      const newCenter = getCenter(p1, p2);
      const dist = getDistance(p1, p2);

      if (!lastTouchDistRef.current) {
        lastTouchDistRef.current = dist;
      }

      const pointTo = {
        x: (newCenter.x - stage.x()) / stage.scaleX(),
        y: (newCenter.y - stage.y()) / stage.scaleX()
      };

      let newScale = stage.scaleX() * (dist / lastTouchDistRef.current);
      newScale = Math.min(Math.max(newScale, MIN_SCALE), MAX_SCALE);

      stage.scale({ x: newScale, y: newScale });

      const dx = newCenter.x - lastTouchCenterRef.current.x;
      const dy = newCenter.y - lastTouchCenterRef.current.y;

      const newPos = {
        x: newCenter.x - pointTo.x * newScale + dx,
        y: newCenter.y - pointTo.y * newScale + dy
      };

      stage.position(newPos);

      lastTouchDistRef.current = dist;
      lastTouchCenterRef.current = newCenter;
    }
  };

  /** 📱 Reset touch data when fingers lift */
  const handleTouchEnd = (event: Konva.KonvaEventObject<TouchEvent>) => {
    if (event.evt.touches.length === 0) {
      // No fingers left, reset zoom state
      pinchZoomingRef.current = false;
      lastTouchDistRef.current = 0;
      lastTouchCenterRef.current = null;
      stageRef.current?.draggable(true); // Re-enable dragging after zoom
    }
  };

  /** 🔄 Dragging (Pan) */
  const handleDragMove = (event: Konva.KonvaEventObject<DragEvent>) => {
    if (pinchZoomingRef.current) return; // Prevent panning during pinch-to-zoom
  };

  const handleDragEnd = (event: Konva.KonvaEventObject<DragEvent>) => {
    if (pinchZoomingRef.current) return;
    const newPos = event.target.position();
    updateStagePosition({
      x: newPos.x,
      y: newPos.y,
      scale: stagePosition.scale
    });
  };

  const handleMouseDown = (event: Konva.KonvaPointerEvent) => {
    if (pinchZoomingRef.current) return;
  };

  const handleMouseMove = (event: Konva.KonvaPointerEvent) => {
    if (pinchZoomingRef.current) return;
  };

  return {
    onWheel: handleWheel,
    onDragMove: handleDragMove,
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onDragEnd: handleDragEnd,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    cursor
  };
};
